import React from "react";
import styled, { keyframes, css } from "styled-components";

type AgentTransferState = "transferring" | "connected";

interface AgentTransferDividerProps {
  fromAgent: string;
  toAgent: string;
  state: AgentTransferState;
}

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  font-size: 11px;
  color: #64748b; /* slate-500 */
`;

const Line = styled.span`
  flex: 1;
  height: 1px;
  background-color: #e5e7eb; /* slate-200 */
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
`;

const dotFade = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 0.3; }
`;

const Dot = styled.span<{ $delay: number }>`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background-color: #4b5563; /* slate-600 */
  opacity: 0.6;
  animation: ${dotFade} 1.4s infinite;
  animation-delay: ${(p) => p.$delay}s;
`;

const glowAgent = keyframes`
  0% { text-shadow: 0 0 0px rgba(79, 70, 229, 0.0); }
  50% { text-shadow: 0 0 10px rgba(79, 70, 229, 0.7); }
  100% { text-shadow: 0 0 0px rgba(79, 70, 229, 0.0); }
`;

const AgentName = styled.span<{ $state: AgentTransferState }>`
  font-weight: 600;
  ${(p) =>
    p.$state === "transferring"
      ? css`
          color: #4f46e5; /* indigo-600 */
          animation: ${glowAgent} 1.6s ease-in-out infinite;
        `
      : css`
          color: #047857; /* emerald-600 */
        `}
`;

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  font-size: 10px;
`;

const AgentTransferDivider: React.FC<AgentTransferDividerProps> = ({
  fromAgent,
  toAgent,
  state,
}) => {
  const isTransferring = state === "transferring";

  return (
    <Row>
      <Line />
      <Pill>
        <span className="font-medium text-slate-700">Agent switch</span>
        <span style={{ color: "#9ca3af" }}>·</span>

        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontWeight: 500, color: "#4b5563" }}>{fromAgent}</span>
          <span style={{ color: "#9ca3af" }}>➜</span>
          <AgentName $state={state}>{toAgent}</AgentName>
        </span>

        {isTransferring ? (
          <Status>
            <Dot $delay={0} />
            <Dot $delay={0.2} />
            <Dot $delay={0.4} />
          </Status>
        ) : (
          <Status style={{ color: "#059669" }}>
            <svg
              width={12}
              height={12}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 0 1 0 1.414l-7.25 7.25a1 1 0 0 1-1.414 0l-3.25-3.25a1 1 0 1 1 1.414-1.414L8.5 11.086l6.543-6.543a1 1 0 0 1 1.414 0Z"
                clipRule="evenodd"
              />
            </svg>
            <span>Connected</span>
          </Status>
        )}
      </Pill>
      <Line />
    </Row>
  );
};

export default AgentTransferDivider;

